import { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { PHRASE_TYPES, useCreateConversationPhrase } from '@/entities/conversations';
import { buildNewRecordFormValues, buildSplitRequest } from './recordForm';
import { useTranscriptEditorContext } from './TranscriptEditorContext';
import type { TranscriptRecordFormValues } from './types';
import { useTranscriptRecordForm } from './useTranscriptRecordForm';

/** Реплика пары, отрезок которой выделен на шкале записи: та, к полям которой пользователь перешёл последней */
type SplitTimelineTarget = 'source' | 'split';

/**
 * Вынос фрагмента из реплики, открытой на правку: фрагмент уходит в новую реплику под исходной, и
 * обе правятся вместе — одним сохранением, одним запросом, одной отменой.
 */
export const useTranscriptPhraseSplit = (sourceId: number, sourceForm: UseFormReturn<TranscriptRecordFormValues>) => {
  const { conversationId, closeForm } = useTranscriptEditorContext();
  const { mutate: createPhrase, isPending } = useCreateConversationPhrase();
  const splitForm = useTranscriptRecordForm(
    PHRASE_TYPES.PHRASE,
    buildNewRecordFormValues({ startedAt: null, finishedAt: null }),
  );
  const [isSplitOpen, setIsSplitOpen] = useState(false);
  const [timelineTarget, setTimelineTarget] = useState<SplitTimelineTarget>('source');

  /** Новая реплика получает фрагмент и границы исходной, говорящий не выбран */
  const openSplit = ({ fragment, rest }: { fragment: string; rest: string }) => {
    const { startedAt, finishedAt } = sourceForm.getValues();
    sourceForm.setValue('text', rest, { shouldDirty: true });
    splitForm.reset(buildNewRecordFormValues({ startedAt, finishedAt }, fragment));
    setIsSplitOpen(true);
  };

  const saveSplit = async () => {
    // Проверяются обе формы сразу — чтобы ошибки были видны у обеих реплик, а не по очереди
    const [isSourceValid, isSplitValid] = await Promise.all([sourceForm.trigger(), splitForm.trigger()]);
    if (!isSourceValid || !isSplitValid) return;
    const data = buildSplitRequest(
      sourceId,
      sourceForm.getValues(),
      sourceForm.formState.dirtyFields,
      splitForm.getValues(),
    );
    createPhrase({ conversationId, data }, { onSuccess: closeForm });
  };

  return {
    splitForm,
    isSplitOpen,
    isSaving: isPending,
    openSplit,
    saveSplit: () => void saveSplit(),
    timelineTarget,
    setTimelineTarget,
  };
};
